import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import {
  ButtonBox,
  CloseButton,
  CodeBox,
  ConfirmationModalHeader,
  ConfirmationModalSubTitle,
  ConfirmationModalTitle,
  ErrorBox,
  ErrorIconBox,
  ErrorText,
  InputBox,
  Spacer,
  StyledCodeInput,
  StyledModal,
} from './styles'
import React, { useState } from 'react'
import { DashboardhMediumButton, Icon } from 'bd-web-components'
import {
  PRIMARY_BLUE_03,
  PRIMARY_RED_01,
  SECONDARY_GREEN_07,
} from 'bd-web-components/src/utils/dash-colors'
import Loader from '../Loader'
import { use2faValidation } from '../../hooks/2fa'
import useSWRMutation from 'swr/mutation'
import { hasArrayFullFilled } from '../../utils/withdraw-helpers'
import { ConfirmationProps } from '../SucessOrFailModal'
import { SSO_URL } from '../../config'
import { useHasAccess } from '../../hooks/auth'
import useBdAppEventBus, {
  TotpSuccessEvent,
} from '../../hooks/bdapp/useBdAppEventBus'
import useBdApp from '../../hooks/bdapp/useBdApp'

interface ConfirmationModalProps {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  setCode: Dispatch<SetStateAction<string>>
  onRequestClose?: () => void
  setIsConfirmationOpen: (b: boolean) => void
  setIsSuccess: (b: boolean) => void
  setConfirmationMsg: Dispatch<SetStateAction<ConfirmationProps>>
  children?: ReactNode
  submitResponse: () => Promise<{ data: any; error?: any }> | { data: any; error: any }
  submitLabel?: string
  // Pula a pré-validação no serviço "two-factor" quando o endpoint de destino
  // já valida o código 2FA por conta própria (ex.: exportMailing).
  skip2faPreValidation?: boolean
}

export const ConfirmationModal = ({
  isOpen,
  setIsOpen,
  setCode,
  setIsConfirmationOpen,
  setIsSuccess,
  setConfirmationMsg,
  onRequestClose,
  submitResponse,
  submitLabel = 'Confirmar Saque',
  skip2faPreValidation = false,
}: ConfirmationModalProps) => {
  const ssoIframeRef = useRef<HTMLIFrameElement>(null)
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', ''])
  const [isError, setIsError] = useState<boolean>(false)
  const clearCode = otp.join().replaceAll(',', '')
  const { fetcher } = use2faValidation()
  const { trigger, isMutating } = useSWRMutation(clearCode, fetcher)
  const ssoAccess = useHasAccess('SSO')
  const { isNative } = useBdApp()
  const { on: subscribeToEventBus, publish } = useBdAppEventBus()
  const shouldShowSsoModal = ssoAccess && !isNative
  const isNativeTotpOpen = useRef<boolean>(false)

  const handleChange = (element: any, index: number) => {
    if (isNaN(element.value)) return false
    setOtp([
      ...otp.map((data: any, idx: any) =>
        idx === index ? element.value : data
      ),
    ])
    if (element.nextSibling) {
      element.nextSibling.focus()
    }
  }

  const handleSetConfirmationMsg = useCallback(
    (prevState: ConfirmationProps): ConfirmationProps => ({
      ...prevState,
      customError: {
        title: 'O código de confirmação expirou!',
        subtitle: 'Tente novamente!',
      },
    }),
    []
  )

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [setIsOpen])

  const handleClear = React.useCallback(() => {
    setOtp(Array(otp.length).fill(''))
    setIsError(false)
  }, [otp, setOtp])

  const handlerWithdraw = useCallback(async () => {
    try {
      const result = await submitResponse()
      const { data } = result
      if (data.success) {
        setIsOpen(false)
        setIsSuccess(true)
      }
    } catch (error: any) {
      const { response } = error
      const { data } = response
      if (data.errorCode === 'CODE_INVALID') {
        setConfirmationMsg(handleSetConfirmationMsg)
      }
      setIsOpen(false)
      setIsSuccess(false)
    }
    handleClose()
  }, [
    handleClose,
    handleSetConfirmationMsg,
    setConfirmationMsg,
    setIsOpen,
    setIsSuccess,
    submitResponse,
  ])

  const handleTotpCancel = useCallback(() => {
    handleClose()
  }, [handleClose])

  const handleTotpFailed = useCallback(() => {
    setConfirmationMsg(handleSetConfirmationMsg)
  }, [handleSetConfirmationMsg, setConfirmationMsg])

  const handleTotpSuccess = useCallback(
    (token: string) => {
      setCode(token)
      setIsConfirmationOpen(true)
      setTimeout(handlerWithdraw, 300)
    },
    [handlerWithdraw, setCode, setIsConfirmationOpen]
  )

  const onSubmit = useCallback(async () => {
    try {
      setCode(clearCode)
      // Quando o endpoint de destino já valida o 2FA (ex.: exportMailing),
      // pulamos a pré-validação no serviço "two-factor" e submetemos direto.
      // O setTimeout dá tempo do estado `code` propagar antes do submit
      // (mesmo padrão usado no fluxo SSO/TOTP acima).
      if (skip2faPreValidation) {
        setIsConfirmationOpen(true)
        setTimeout(handlerWithdraw, 300)
        return
      }
      const result = await trigger()
      const { data } = result
      if (data.success) {
        setIsConfirmationOpen(true)
        await handlerWithdraw()
      }
    } catch (e) {
      setCode('')
      setIsError(true)
      setTimeout(() => handleClear(), 700)
    }
  }, [
    clearCode,
    handlerWithdraw,
    setIsConfirmationOpen,
    trigger,
    setCode,
    handleClear,
    skip2faPreValidation,
  ])

  const handlerPaste = (e: any) => {
    const pasteData = e.clipboardData.getData('Text')
    const pastedArray = pasteData.split('')
    const slicedPast = pastedArray.slice(0, 6)
    setOtp(slicedPast)
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (event.key === 'Backspace' && otp[index] === '') {
      if (index > 0) {
        const previousInput = event.currentTarget.parentElement?.querySelector(
          `input:nth-child(${index})`
        ) as HTMLInputElement
        if (previousInput) {
          previousInput.focus()
        }
      }
    }
  }

  const hasLastIndex = hasArrayFullFilled(otp)

  useEffect(() => {
    const fullOpt = hasArrayFullFilled(otp)
    if (fullOpt) {
      onSubmit()
    }
  }, [otp, onSubmit])

  useEffect(() => {
    if (!shouldShowSsoModal) {
      return
    }

    const handleMessage = (event: MessageEvent) => {
      if (!SSO_URL.includes(event.origin)) {
        console.warn('Received message from an untrusted origin:', event.origin)
        return
      }

      if (
        event.data.type === 'cancel_totp' ||
        event.data.type === 'cancel-totp'
      ) {
        handleTotpCancel()
      }

      if (
        event.data.type === 'failed_totp' ||
        event.data.type === 'failed-totp'
      ) {
        handleTotpFailed()
      }

      if (
        event.data.type === 'success_totp' ||
        event.data.type === 'success-totp'
      ) {
        const token = event.data?.data?.token
        if (token) {
          handleTotpSuccess(token)
        }
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [
    handleTotpCancel,
    handleTotpFailed,
    handleTotpSuccess,
    shouldShowSsoModal,
  ])

  useEffect(() => {
    if (!ssoAccess || !isNative) {
      isNativeTotpOpen.current = false
      return
    }

    if (!isOpen) {
      if (isNativeTotpOpen.current) {
        publish('cancel-totp', { reason: 'modal-closed' })
      }
      isNativeTotpOpen.current = false
      return
    }

    if (isNativeTotpOpen.current) {
      return
    }

    isNativeTotpOpen.current = true

    publish('open-totp', {});
  }, [isOpen, isNative, publish, ssoAccess])

  useEffect(() => {
    if (!ssoAccess || !isNative) {
      return
    }

    const unsubscribeCancel = subscribeToEventBus('cancel-totp', () => {
      handleTotpCancel()
    })

    const unsubscribeFailed = subscribeToEventBus('failed-totp', () => {
      handleTotpFailed()
    })

    const unsubscribeSuccess = subscribeToEventBus<TotpSuccessEvent>(
      'success-totp',
      (data) => {
        if (data?.token) {
          handleTotpSuccess(data.token)
        }
      }
    )

    return () => {
      unsubscribeCancel()
      unsubscribeFailed()
      unsubscribeSuccess()
    }
  }, [
    handleTotpCancel,
    handleTotpFailed,
    handleTotpSuccess,
    isNative,
    ssoAccess,
    subscribeToEventBus,
  ])

  return (
    <>
      {!ssoAccess && (
        <StyledModal
          isOpen={isOpen}
          onRequestClose={onRequestClose}
          style={{
            overlay: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          }}
        >
          <ConfirmationModalHeader>
            <div>
              <ConfirmationModalTitle>
                Insira o código de autenticação da transação
              </ConfirmationModalTitle>
              <ConfirmationModalSubTitle>
                Utilize o seu código do Google Authenticator para validar sua
                solicitação.
              </ConfirmationModalSubTitle>
            </div>
            <div>
              <CloseButton
                // @ts-ignore
                onClick={handleClose}
                type="button"
              >
                <Icon
                  width={16}
                  height={16}
                  iconName={'x'}
                  fill={PRIMARY_BLUE_03}
                  classes="add-account-chevron"
                />
              </CloseButton>
            </div>
          </ConfirmationModalHeader>
          <InputBox error={isError}>
            <CodeBox>
              {otp.map((item: string, index: number) => (
                <StyledCodeInput
                  error={isError}
                  key={index}
                  type="text"
                  maxLength={1}
                  value={item}
                  onChange={(e) => handleChange(e.target, index)}
                  onPaste={handlerPaste}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                />
              ))}
            </CodeBox>
            {isError && (
              <ErrorBox>
                <ErrorIconBox>
                  <Icon
                    width={10}
                    height={10}
                    iconName={'x'}
                    fill={PRIMARY_RED_01}
                    classes="error-icon-code"
                  />
                </ErrorIconBox>
                <ErrorText>
                  <span className="button__error">
                    {'Código inválido - Tente novamente'}
                  </span>
                </ErrorText>
              </ErrorBox>
            )}
          </InputBox>
          <br />
          <ButtonBox>
            <DashboardhMediumButton
              onClick={onSubmit}
              //@ts-ignore
              type="button"
              background={
                hasLastIndex && !isMutating ? SECONDARY_GREEN_07 : 'disabled'
              }
              color=""
              height="52px"
              maxWidth="100%"
              variant={
                hasLastIndex && !isMutating && isError
                  ? 'contained'
                  : 'disabled'
              }
            >
              {!isMutating && (
                <span className="button__submit_code">{submitLabel}</span>
              )}
              {isMutating && <Loader small={true} />}
            </DashboardhMediumButton>
            <br />
            <Spacer />
            <DashboardhMediumButton
              onClick={handleClear}
              //@ts-ignore
              type="button"
              background=""
              color=""
              height="52px"
              maxWidth="100%"
              variant={'outlined'}
            >
              <span className="button__clean">{'Limpar'}</span>
            </DashboardhMediumButton>
          </ButtonBox>
        </StyledModal>
      )}

      {shouldShowSsoModal && (
        <StyledModal
          isOpen={isOpen}
          onRequestClose={onRequestClose}
          style={{
            overlay: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          }}
        >
          <iframe
            ref={ssoIframeRef}
            title="sso-2fa"
            src={SSO_URL + 'totp-embed'}
            style={{
              width: '100%',
              height: '400px',
              border: 'none',
            }}
          />
        </StyledModal>
      )}
    </>
  )
}
